import * as THREE from 'three';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';

// ---------------------------------------------------------------------------
// Lightmap Loader
// ---------------------------------------------------------------------------
// Loads baked lightmap textures (KTX2 or standard image formats) with caching.
// Lightmaps use UV channel 1 (uv2 / TEXCOORD_1) and follow glTF flip convention.
// ---------------------------------------------------------------------------

export class LightmapLoader {
  private readonly ktx2Loader: KTX2Loader;
  private readonly textureLoader: THREE.TextureLoader;
  private readonly cache: Map<string, THREE.Texture> = new Map();

  constructor(renderer: THREE.WebGLRenderer) {
    this.ktx2Loader = new KTX2Loader()
      .setTranscoderPath('/libs/basis/')
      .detectSupport(renderer);
    this.textureLoader = new THREE.TextureLoader();
  }

  /**
   * Load a lightmap texture from the given path.
   * KTX2 files are loaded via the Basis Universal transcoder; all other
   * formats fall back to the standard THREE.TextureLoader.
   *
   * Loaded textures are cached by path — repeated calls return the same
   * texture instance without hitting the network.
   */
  async loadLightmap(path: string): Promise<THREE.Texture> {
    const cached = this.cache.get(path);
    if (cached) return cached;

    const ext = path.split('.').pop()?.toLowerCase();

    const texture = await new Promise<THREE.Texture>((resolve, reject) => {
      if (ext === 'ktx2') {
        this.ktx2Loader.load(
          path,
          (tex: THREE.Texture) => resolve(tex),
          undefined,
          reject,
        );
      } else {
        this.textureLoader.load(
          path,
          (tex: THREE.Texture) => resolve(tex),
          undefined,
          reject,
        );
      }
    });

    // Lightmaps sample from UV channel 1 (uv2 / TEXCOORD_1)
    texture.channel = 1;
    // glTF convention — lightmaps baked in DCC tools with glTF export don't flip Y
    texture.flipY = false;
    // Lightmap data is linear (light intensity values, not sRGB color)
    texture.colorSpace = THREE.LinearSRGBColorSpace;

    this.cache.set(path, texture);
    return texture;
  }

  /**
   * Check whether a lightmap file exists at the given URL without fully
   * downloading it. Uses a HEAD request so the cost is negligible.
   */
  async exists(path: string): Promise<boolean> {
    try {
      const response = await fetch(path, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  }

  /** Dispose all cached textures and the KTX2 transcoder. */
  dispose(): void {
    this.cache.forEach((t) => t.dispose());
    this.cache.clear();
    this.ktx2Loader.dispose();
  }
}
